/**
 * Created: 2026-09-27
 * Initial commit: Split 课程管理 menu into 课程发布/已售课时 submenus
 *
 * 已售课时 page: lists all course entitlements sold at the current site,
 * with a user picker to filter by buyer.
 *
 * Updated: 2026-10-14 - Status header filter on the grid, applied server-side
 * Updated: 2026-10-15 - Click a row to open its detail dialog
 * Updated: 2026-10-18 - Status filter defaults to ACTIVE (valid entitlements)
 * Updated: 2026-10-29 - Reload the list after a refund starts; a cancelled
 * entitlement has no remaining hours
 */

import { gql } from '@apollo/client'
import { useLazyQuery } from '@apollo/client/react'
import { Alert, Box, Button, Chip, Typography } from '@mui/material'
import {
  DataGridPro,
  type GridColDef,
  type GridFilterModel,
  type GridRowParams,
  getGridSingleSelectOperators,
} from '@mui/x-data-grid-pro'
import { remainingHoursOf } from '@tc-app/shared/course/entitlement'
import { COURSE_ENTITLEMENT_STATUS_CHIP, formatClassSize } from '@tc-app/shared/labels'
import { useCallback, useEffect, useRef, useState } from 'react'
import EntitlementDialog from '../components/EntitlementDialog'
import UserAvatar from '../components/UserAvatar'
import UserSelector, { type UserSelectorUser } from '../components/UserSelector'
import {
  CourseEntitlementStatus,
  CourseEntitlementsDocument,
  type CourseEntitlementsQuery,
} from '../gql/graphql'
import { useRole } from '../hooks'

gql`
  query CourseEntitlements(
    $filter: CourseEntitlementFilterInput
    $first: Int
    $after: String
  ) {
    courseEntitlements(filter: $filter, first: $first, after: $after) {
      edges {
        node {
          id
          orderId
          courseName
          seats
          sport
          totalHours
          consumedHours
          validUntil
          status
          createTime
          user {
            name
            avatar
            gender
            openid
            mobile
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`

type EntitlementRow =
  CourseEntitlementsQuery['courseEntitlements']['edges'][number]['node']

const columns: GridColDef<EntitlementRow>[] = [
  {
    field: '__rowNumber',
    headerName: '#',
    width: 50,
    sortable: false,
    filterable: false,
    align: 'center',
    headerAlign: 'center',
    renderCell: (params) => params.api.getRowIndexRelativeToVisibleRows(params.id) + 1,
  },
  {
    field: 'user',
    headerName: '客户',
    width: 180,
    sortable: false,
    filterable: false,
    headerAlign: 'center',
    renderCell: ({ row }) => <UserAvatar user={row.user} showMobile={true} />,
  },
  {
    field: 'courseName',
    headerName: '课程',
    flex: 1,
    minWidth: 160,
    sortable: false,
    filterable: false,
    headerAlign: 'center',
  },
  {
    field: 'seats',
    headerName: '班型',
    valueFormatter: (value: number) => formatClassSize(value),
    width: 90,
    sortable: false,
    filterable: false,
    align: 'center',
    headerAlign: 'center',
  },
  {
    field: 'totalHours',
    headerName: '总课时',
    width: 90,
    sortable: false,
    filterable: false,
    align: 'center',
    headerAlign: 'center',
  },
  {
    field: 'remainingHours',
    headerName: '剩余课时',
    width: 90,
    sortable: false,
    filterable: false,
    align: 'center',
    headerAlign: 'center',
    valueGetter: (_value, row) => remainingHoursOf(row),
  },
  {
    field: 'createTime',
    headerName: '购买时间',
    width: 170,
    sortable: false,
    filterable: false,
    headerAlign: 'center',
    valueGetter: (value: string) => new Date(value).toLocaleString('zh-CN'),
  },
  {
    field: 'validUntil',
    headerName: '有效期至',
    width: 120,
    sortable: false,
    filterable: false,
    align: 'center',
    headerAlign: 'center',
  },
  {
    field: 'status',
    headerName: '状态',
    width: 120,
    sortable: false,
    type: 'singleSelect',
    valueOptions: Object.values(CourseEntitlementStatus).map((value) => ({
      value,
      label: COURSE_ENTITLEMENT_STATUS_CHIP[value].label,
    })),
    // The backend filters on a single status, so only offer "is".
    filterOperators: getGridSingleSelectOperators().filter((op) => op.value === 'is'),
    align: 'center',
    headerAlign: 'center',
    renderCell: ({ row }) => {
      const st = COURSE_ENTITLEMENT_STATUS_CHIP[row.status]
      return <Chip label={st.label} color={st.color} size="small" />
    },
  },
]

// Default to showing only valid (ACTIVE) entitlements.
const DEFAULT_FILTER_MODEL: GridFilterModel = {
  items: [{ field: 'status', operator: 'is', value: CourseEntitlementStatus.Active }],
}

export default function CourseEntitlementsPage() {
  const { currentSite } = useRole()
  const [selectedUser, setSelectedUser] = useState<UserSelectorUser | null>(null)
  const [filterModel, setFilterModel] = useState<GridFilterModel>(DEFAULT_FILTER_MODEL)
  const [selected, setSelected] = useState<EntitlementRow | null>(null)

  const [edges, setEdges] = useState<
    CourseEntitlementsQuery['courseEntitlements']['edges']
  >([])
  const [pageInfo, setPageInfo] = useState<
    CourseEntitlementsQuery['courseEntitlements']['pageInfo']
  >({ __typename: 'PageInfo', hasNextPage: false, endCursor: null })

  const [execute, { loading, error, fetchMore }] = useLazyQuery(
    CourseEntitlementsDocument,
    { fetchPolicy: 'network-only' },
  )

  const executeRef = useRef(execute)
  executeRef.current = execute

  const loadingMore = useRef(false)
  const lastFetchedCursor = useRef<string | null | undefined>(undefined)

  const initialLoaded = useRef(false)
  useEffect(() => {
    if (currentSite && !initialLoaded.current) {
      initialLoaded.current = true
      executeRef
        .current({
          variables: {
            filter: {
              siteId: currentSite.id,
              status: CourseEntitlementStatus.Active,
              adminView: true,
            },
          },
        })
        .then((result) => {
          if (result.data) {
            setEdges(result.data.courseEntitlements.edges)
            setPageInfo(result.data.courseEntitlements.pageInfo)
          }
        })
    }
  }, [currentSite])

  const search = (user: UserSelectorUser | null, model: GridFilterModel) => {
    const status = model.items.find((item) => item.field === 'status')?.value as
      | CourseEntitlementStatus
      | undefined
    loadingMore.current = false
    lastFetchedCursor.current = undefined
    setEdges([])
    setPageInfo({ __typename: 'PageInfo', hasNextPage: false, endCursor: null })
    execute({
      variables: {
        filter: {
          siteId: currentSite?.id as string,
          mobile: user?.mobile ?? undefined,
          status: status || undefined,
          adminView: true,
        },
      },
    }).then((result) => {
      if (result.data) {
        setEdges(result.data.courseEntitlements.edges)
        setPageInfo(result.data.courseEntitlements.pageInfo)
      }
    })
  }

  const handleSearch = () => search(selectedUser, filterModel)

  const handleFilterModelChange = (model: GridFilterModel) => {
    setFilterModel(model)
    search(selectedUser, model)
  }

  const handleRowsScrollEnd = useCallback(() => {
    if (
      pageInfo.hasNextPage &&
      !loadingMore.current &&
      pageInfo.endCursor !== lastFetchedCursor.current
    ) {
      loadingMore.current = true
      lastFetchedCursor.current = pageInfo.endCursor
      fetchMore({
        variables: { after: pageInfo.endCursor },
      }).then((result) => {
        const more = result.data
        if (more) {
          setEdges((prev) => {
            const existingIds = new Set(prev.map((e) => e.node.id))
            const newEdges = more.courseEntitlements.edges.filter(
              (e) => !existingIds.has(e.node.id),
            )
            return [...prev, ...newEdges]
          })
          setPageInfo(more.courseEntitlements.pageInfo)
        }
        loadingMore.current = false
      })
    }
  }, [pageInfo, fetchMore])

  const rows = edges.map((e) => e.node)

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Typography variant="h4">已售课时</Typography>
        <Box sx={{ flex: 1 }} />
        <UserSelector
          siteId={currentSite?.id as string}
          value={selectedUser}
          onChange={setSelectedUser}
        />
        <Button variant="contained" size="small" onClick={handleSearch}>
          搜索
        </Button>
      </Box>

      {error && <Alert severity="error">加载课时失败: {error.message}</Alert>}

      {initialLoaded.current && (
        <DataGridPro
          rows={rows}
          columns={columns}
          getRowId={(row: EntitlementRow) => row.id}
          loading={loading}
          hideFooter
          disableRowSelectionOnClick
          disableColumnMenu
          headerFilters
          filterMode="server"
          filterModel={filterModel}
          onFilterModelChange={handleFilterModelChange}
          density="compact"
          getRowHeight={() => 'auto'}
          onRowsScrollEnd={handleRowsScrollEnd}
          onRowClick={({ row }: GridRowParams<EntitlementRow>) => setSelected(row)}
          localeText={{ noRowsLabel: '暂无课时', filterValueAny: '全部' }}
          sx={{
            flex: 1,
            minHeight: 0,
            '& .MuiDataGrid-row': { cursor: 'pointer' },
            '& .MuiDataGrid-cell': {
              display: 'flex',
              alignItems: 'center',
              py: 1,
            },
          }}
        />
      )}

      <EntitlementDialog
        entitlement={selected}
        onClose={() => setSelected(null)}
        onRefunded={handleSearch}
      />
    </Box>
  )
}
